import { Contact } from '../types/Contact';
import type { SharePreferences } from './sharePrefs';

export interface ShareContactData {
    first_name?: string;
    last_name?: string;
    title?: string;
    company?: string;
    email?: string;
    phone?: string;
    mobile?: string;
    business_email?: string;
    business_phone?: string;
    email_1_value?: string;
    email_2_value?: string;
    email_3_value?: string;
    phone_1_value?: string;
    phone_2_value?: string;
    phone_3_value?: string;
    linkedin_profile_url?: string;
    profile_image_url?: string;
    notes?: string;
}

export const generateVCard = (formData: ShareContactData): string => {
    const vCard = [
        'BEGIN:VCARD',
        'VERSION:3.0',
        `FN:${formData.first_name || ''} ${formData.last_name || ''}`,
        `N:${formData.last_name || ''};${formData.first_name || ''};;;`,
    ];

    // Add organization - ONLY basic company info, no proprietary details
    if (formData.company) {
        vCard.push(`ORG:${formData.company}`);
    }
    if (formData.title) {
        vCard.push(`TITLE:${formData.title}`);
    }

    // Add ONLY primary email and phone - no internal business details
    if (formData.email) {
        vCard.push(`EMAIL;TYPE=WORK:${formData.email}`);
    }

    if (formData.phone) {
        vCard.push(`TEL;TYPE=WORK:${formData.phone}`);
    }

    if (formData.mobile && formData.mobile !== formData.phone) {
        vCard.push(`TEL;TYPE=CELL:${formData.mobile}`);
    }

    // Add LinkedIn URL - public info only
    if (formData.linkedin_profile_url) {
        vCard.push(`URL:${formData.linkedin_profile_url}`);
    }

    // NEVER include notes or proprietary information in vCard

    vCard.push('END:VCARD');
    return vCard.join('\n');
};

export const generateContactCard = (formData: ShareContactData): string => {
    const contactName = `${formData.first_name || ''} ${formData.last_name || ''}`.trim() || 'Contact';
    const title = formData.title || '';
    const company = formData.company || '';
    const email = formData.email || '';
    const phone = formData.phone || '';
    const mobile = formData.mobile || '';
    const linkedin = formData.linkedin_profile_url || '';
    const profileImage = formData.profile_image_url || '';

    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${contactName} - Business Card</title>
    <style>
        @page {
            size: 3.5in 2in;
            margin: 0;
        }
        
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #000;
            color: #fff;
            width: 3.5in;
            height: 2in;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0;
            margin: 0;
        }
        
        .business-card {
            background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
            width: 100%;
            height: 100%;
            display: flex;
            padding: 20px;
            position: relative;
            overflow: hidden;
        }
        
        .business-card::before {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            width: 60px;
            height: 60px;
            background: linear-gradient(45deg, #667eea, #764ba2);
            border-radius: 0;
        }
        
        .left-section {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            z-index: 2;
        }
        
        .right-section {
            width: 80px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            z-index: 2;
        }
        
        .avatar {
            width: 60px;
            height: 60px;
            border-radius: 0;
            overflow: hidden;
            margin-bottom: 10px;
            border: 2px solid #667eea;
            ${profileImage ?
            `background-image: url('${profileImage}'); background-size: cover; background-position: center;` :
            `background: linear-gradient(135deg, #667eea, #764ba2); display: flex; align-items: center; justify-content: center; color: white; font-size: 24px; font-weight: bold;`
        }
        }
        
        .name {
            font-size: 18px;
            font-weight: 700;
            color: #fff;
            margin-bottom: 4px;
            line-height: 1.2;
        }
        
        .title-company {
            font-size: 11px;
            color: #a1a1aa;
            margin-bottom: 12px;
            line-height: 1.3;
        }
        
        .contact-info {
            font-size: 9px;
            color: #d4d4d8;
            line-height: 1.4;
        }
        
        .contact-item {
            margin-bottom: 3px;
            display: flex;
            align-items: center;
        }
        
        .contact-icon {
            margin-right: 6px;
            font-size: 8px;
        }
        
        @media print {
            body {
                background: #000 !important;
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
            }
            
            .business-card {
                background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%) !important;
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
            }
        }
    </style>
</head>
<body>
    <div class="business-card">
        <div class="left-section">
            <div class="name">${contactName}</div>
            ${title || company ? `<div class="title-company">${title}${title && company ? ' at ' : ''}${company}</div>` : ''}
            
            <div class="contact-info">
                ${email ? `
                <div class="contact-item">
                    <span class="contact-icon">📧</span>
                    <span>${email}</span>
                </div>
                ` : ''}
                ${phone ? `
                <div class="contact-item">
                    <span class="contact-icon">📞</span>
                    <span>${phone}</span>
                </div>
                ` : ''}
                ${mobile && mobile !== phone ? `
                <div class="contact-item">
                    <span class="contact-icon">📱</span>
                    <span>${mobile}</span>
                </div>
                ` : ''}
                ${linkedin ? `
                <div class="contact-item">
                    <span class="contact-icon">🔗</span>
                    <span>${linkedin.replace('https://', '')}</span>
                </div>
                ` : ''}
            </div>
        </div>
        
        <div class="right-section">
            <div class="avatar">${profileImage ? '' : contactName.charAt(0).toUpperCase()}</div>
        </div>
    </div>
</body>
</html>`;
};

// Extended formatting: optionally accepts sender info + location for personalization
export interface FormatContactTextExtras {
    senderName?: string | null; // Your name (auto inserted as first sentence)
    meetingLocationLabel?: string | null; // human-readable location (e.g., "Austin, TX")
}

export const formatContactText = (
    formData: ShareContactData,
    prefs?: Partial<SharePreferences>,
    extras?: FormatContactTextExtras
): string => {
    const contactName = `${formData.first_name || ''} ${formData.last_name || ''}`.trim();
    // Supported placeholders: {name}, {my_name}, {location}
    const template = (prefs as any)?.template as string | undefined;
    const defaultBase = `Hey {name}. It's {my_name}. It was great meeting you at {location}. We should hang out sometime.`;
    let out = (template && template.trim()) ? template : defaultBase;
    const sender = extras?.senderName || 'me';
    const loc = extras?.meetingLocationLabel || '';
    out = out.replace(/\{name\}/gi, contactName || 'you');
    out = out.replace(/\{my_name\}/gi, sender);
    out = out.replace(/\{location\}/gi, loc);
    // Remove any unsupported legacy tokens (e.g., {details})
    out = out.replace(/\{details\}/gi, '');
    out = out.replace(/\{[^}]+\}/g, '');
    // Normalize whitespace
    out = out.replace(/\s+\n/g, '\n').replace(/\n{3,}/g, '\n\n');
    return out.trim();
};
